var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect, useCallback } from 'react';
import { Trans } from 'react-i18next';
import { observer } from 'mobx-react';
import { Button, Spin, Radio, Typography, Form, message, Alert } from 'antd';
import { RiCheckboxMultipleLine, RiCheckLine, RiErrorWarningLine } from 'react-icons/ri';
import { Permissions, CHAT_STATUS } from '@whatsper/texterchat-common';
import { pick, debounce } from 'lodash';
import ModalWithoutBottom from '../../../../Chat/common/ModalWithoutBottom';
import { useHistory } from 'react-router-dom';
import useCheckIfMobile from '../../../../../helpers/hooks/useCheckIfMobile';
import { useStore } from '../../../../../store/rootStore';
import { MyInput } from '../../../../Base/Form';
import { RESOLVE_HOURS } from '../../../../../constants';
import s from './ResolvePendingChats.module.scss';
const { Text } = Typography;
const ResolvePendingChats = () => {
    const isMobile = useCheckIfMobile();
    const history = useHistory();
    const { agentsStore, chatsStore } = useStore();
    const [submitting, setSubmitting] = useState(false);
    const [pendingHours, setPendingHours] = useState(36);
    const [takenHours, setTakenHours] = useState(48);
    const [customPendingHours, setCustomPendingHours] = useState(RESOLVE_HOURS.CUSTOM);
    const [customTakenHours, setCustomTakenHours] = useState(RESOLVE_HOURS.CUSTOM);
    const [pendingCount, setPendingCount] = useState(null);
    const [takenCount, setTakenCount] = useState(null);
    const canManageResolve = agentsStore.hasPermissions([Permissions.Permission.chats_bulk_resolve]);
    const fetchPendingCount = useCallback(debounce((hours) => {
        const beforeTimestamp = Date.now() - hours * 60 * 60 * 1000;
        chatsStore.fetchCount({ beforeTimestamp })
            .then((count) => {
            var _a;
            setPendingCount((_a = count.status[CHAT_STATUS[CHAT_STATUS.PENDING]]) !== null && _a !== void 0 ? _a : 0);
        })
            .catch((error) => {
            console.error('Error fetching pending count', error);
        });
    }, 500), []);
    const fetchTakenCount = useCallback(debounce((hours) => {
        const beforeTimestamp = Date.now() - hours * 60 * 60 * 1000;
        chatsStore.fetchCount({ beforeTimestamp })
            .then((count) => {
            var _a;
            setTakenCount((_a = count.status[CHAT_STATUS[CHAT_STATUS.ASSIGNED]]) !== null && _a !== void 0 ? _a : 0);
        })
            .catch((error) => {
            console.error('Error fetching taken count', error);
        });
    }, 500), []);
    useEffect(() => {
        fetchPendingCount(pendingHours);
        fetchTakenCount(takenHours);
    }, []);
    useEffect(() => {
        const effectiveHours = pendingHours === RESOLVE_HOURS.CUSTOM ? customPendingHours : pendingHours;
        if (effectiveHours > 0) {
            fetchPendingCount(effectiveHours);
        }
    }, [pendingHours, customPendingHours]);
    useEffect(() => {
        const effectiveHours = takenHours === RESOLVE_HOURS.CUSTOM ? customTakenHours : takenHours;
        if (effectiveHours > 0) {
            fetchTakenCount(effectiveHours);
        }
    }, [takenHours, customTakenHours]);
    const onBack = () => {
        history.push('/');
    };
    const onSubmit = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setSubmitting(true);
            const finalPendingHours = pendingHours === RESOLVE_HOURS.CUSTOM ? customPendingHours : pendingHours;
            const finalTakenHours = takenHours === RESOLVE_HOURS.CUSTOM ? customTakenHours : takenHours;
            yield chatsStore.bulkResolve(CHAT_STATUS.PENDING, finalPendingHours);
            yield chatsStore.bulkResolve(CHAT_STATUS.ASSIGNED, finalTakenHours);
            message.success({
                content: React.createElement(Trans, { i18nKey: "resolveSettingsSaved" }),
                icon: (React.createElement(RiCheckLine, { style: { color: '#52c41a', fontSize: '20px', display: 'flex', alignItems: 'center' } })),
                className: s.successMessage,
            });
            onBack();
        }
        catch (error) {
            console.error('Failed to resolve chats', error);
            message.error(React.createElement(Trans, { i18nKey: "resolveSettingsSaveError" }));
        }
        finally {
            setSubmitting(false);
        }
    });
    const onCancel = () => {
        onBack();
    };
    const pendingDropdownOptions = pick(RESOLVE_HOURS, ['24', '36', '48', '72']);
    const takenDropdownOptions = pick(RESOLVE_HOURS, ['12', '24', '36', '72']);
    return (React.createElement(ModalWithoutBottom, { setModalVisible: onBack, titleTextKey: "resolvePendingChats", height: "75vh", width: isMobile ? undefined : 600, visible: true, headerIcon: () => React.createElement(RiCheckboxMultipleLine, { className: s.titleIcon }) },
        React.createElement("div", { className: s.content },
            React.createElement("div", { className: s.formContainer },
                React.createElement(Alert, { className: s.alert, message: pendingCount !== null
                        ? React.createElement(Trans, { i18nKey: "totalOfChatsWillBeResolved", values: { chatsCount: pendingCount }, components: { bold: React.createElement("strong", null) } })
                        : React.createElement(Spin, { size: "small" }), type: "warning", icon: React.createElement(RiErrorWarningLine, null), showIcon: true }),
                React.createElement(Form, { layout: "vertical", className: s.form },
                    React.createElement(Form.Item, { label: React.createElement(Trans, { i18nKey: "resolveAllPendingChatsOlderThan" }) },
                        React.createElement(Radio.Group, { onChange: (e) => setPendingHours(e.target.value), value: pendingHours, disabled: !canManageResolve },
                            Object.keys(pendingDropdownOptions).map((key) => (React.createElement(Radio, { key: key, className: s.radio, value: RESOLVE_HOURS[key] },
                                React.createElement(Text, null,
                                    React.createElement(Trans, { i18nKey: RESOLVE_HOURS[key] === 1 ? 'hour' : 'hoursNumber', values: { number: RESOLVE_HOURS[key] } }))))),
                            React.createElement(Radio, { className: s.radio, value: RESOLVE_HOURS.CUSTOM },
                                React.createElement(Text, null,
                                    React.createElement(Trans, { i18nKey: "custom" })))),
                        pendingHours === RESOLVE_HOURS.CUSTOM && (React.createElement("div", { className: s.inputWrapper },
                            React.createElement(MyInput, { value: customPendingHours, onChangeHandler: (value) => setCustomPendingHours(parseInt(value) || 0), className: s.input, allowClear: true, disabled: !canManageResolve }),
                            React.createElement("div", { className: s.inputLabel },
                                React.createElement(Trans, { i18nKey: "hours" })))))),
                React.createElement(Alert, { className: s.alert, message: takenCount !== null
                        ? React.createElement(Trans, { i18nKey: "totalOfChatsWillBeResolved", values: { chatsCount: takenCount }, components: { bold: React.createElement("strong", null) } })
                        : React.createElement(Spin, { size: "small" }), type: "warning", icon: React.createElement(RiErrorWarningLine, null), showIcon: true }),
                React.createElement(Form, { layout: "vertical", className: s.form },
                    React.createElement(Form.Item, { label: React.createElement(Trans, { i18nKey: "resolveAllTakenChatsOlderThan" }) },
                        React.createElement(Radio.Group, { onChange: (e) => setTakenHours(e.target.value), value: takenHours, disabled: !canManageResolve },
                            Object.keys(takenDropdownOptions).map((key) => (React.createElement(Radio, { key: key, className: s.radio, value: RESOLVE_HOURS[key] },
                                React.createElement(Text, null,
                                    React.createElement(Trans, { i18nKey: RESOLVE_HOURS[key] === 1 ? 'hour' : 'hoursNumber', values: { number: RESOLVE_HOURS[key] } }))))),
                            React.createElement(Radio, { className: s.radio, value: RESOLVE_HOURS.CUSTOM },
                                React.createElement(Text, null,
                                    React.createElement(Trans, { i18nKey: "custom" })))),
                        takenHours === RESOLVE_HOURS.CUSTOM && (React.createElement("div", { className: s.inputWrapper },
                            React.createElement(MyInput, { value: customTakenHours, onChangeHandler: (value) => setCustomTakenHours(parseInt(value) || 0), className: s.input, allowClear: true, disabled: !canManageResolve }),
                            React.createElement("div", { className: s.inputLabel },
                                React.createElement(Trans, { i18nKey: "hours" }))))))),
            React.createElement("div", { className: s.footer },
                React.createElement(Button, { onClick: onCancel, className: s.actionButton, disabled: submitting },
                    React.createElement(Trans, { i18nKey: "cancel" })),
                React.createElement(Button, { type: "primary", danger: true, onClick: onSubmit, className: s.actionButton, loading: submitting, disabled: submitting || !canManageResolve },
                    React.createElement(Trans, { i18nKey: "resolve" }))))));
};
export default observer(ResolvePendingChats);
